import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, Bot, Plus, Send, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useCurrentUser } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingButton } from '@/components/loading-button'
import { cn, formatEventTime } from '@/lib/utils'
import { formatApiErrorMessage } from '@/lib/api-error'
import { ConversationListItemContent } from '@/features/communications/conversation-list'
import { conversationListItemClass } from '@/features/communications/message-format'
import { ChatMessageRow } from '@/features/storefront-chat/chat-message'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { useVisualViewportHeight } from '@/hooks/use-visual-viewport-height'
import {
  type LiveAnswer,
  type StaffChat,
  type StaffChatMessage,
  useAskStaffChat,
  useDeleteStaffChat,
  useStaffChat,
  useStaffChats,
} from '@/features/staff-chat/api'
import { ComposeTextarea } from '@/components/compose-field'

/** The route's chat id for a chat that does not exist yet. */
export const NEW_CHAT = 'new'

function ChatListRow({ chat, selected }: { chat: StaffChat; selected: boolean }) {
  return (
    <li>
      <Link
        to="/customer-comms/store-ai/$chatId"
        params={{ chatId: chat.uuid }}
        aria-current={selected ? 'true' : undefined}
        data-testid={`staff-chat-list-item-${chat.uuid}`}
        className={conversationListItemClass(selected)}
      >
        <ConversationListItemContent
          selected={selected}
          title={<span className="line-clamp-2">{chat.title}</span>}
          aside={
            <time dateTime={chat.last_message_at} className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {formatEventTime(chat.last_message_at)}
            </time>
          }
          meta={null}
        />
      </Link>
    </li>
  )
}

function ChatHeader({ chat, phone }: { chat: StaffChat; phone: boolean }) {
  const navigate = useNavigate()
  const remove = useDeleteStaffChat()
  return (
    <header className={cn('border-b', phone && 'pt-[env(safe-area-inset-top)]')}>
      <div className={cn('flex items-center gap-2 px-3 sm:px-4', phone ? 'py-1.5 pl-1' : 'py-3')}>
        <Link
          to="/customer-comms/store-ai"
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 lg:hidden',
            phone ? 'size-10 text-foreground' : '-ml-1 p-1',
          )}
          aria-label="All chats"
        >
          <ArrowLeft className={phone ? 'h-5 w-5' : 'h-4 w-4'} />
        </Link>
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">{chat.title}</h2>
        <LoadingButton
          loading={remove.isPending}
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Delete chat"
          onClick={() =>
            remove.mutate(chat.uuid, {
              onSuccess: () => void navigate({ to: '/customer-comms/store-ai', replace: true }),
              onError: (err) => toast.error(formatApiErrorMessage('Delete failed', err)),
            })
          }
        >
          <Trash2 className="h-4 w-4" />
        </LoadingButton>
      </div>
    </header>
  )
}

function Messages({
  messages,
  live,
  myName,
}: {
  messages: StaffChatMessage[]
  live: LiveAnswer | null
  myName: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  const last = messages[messages.length - 1]
  // The question shows at once; the saved copy replaces it a moment later.
  const pendingQuestion = live && !(last?.author === 'staff' && last.body === live.question) ? live.question : null

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && pinned.current) el.scrollTop = el.scrollHeight
  }, [last?.uuid, pendingQuestion, live?.text, live?.status])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (pinned.current) el.scrollTop = el.scrollHeight
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => {
        const el = e.currentTarget
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
      }}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      <ol aria-label="Messages" aria-live="polite" className="space-y-1 py-3">
        {messages.map((message) => (
          <li key={message.uuid}>
            <ChatMessageRow
              message={{ ...message, author: message.author === 'staff' ? 'shopper' : 'ai' }}
              shopperName={myName}
            />
          </li>
        ))}
        {pendingQuestion && (
          <li>
            <ChatMessageRow
              message={{ author: 'shopper', body: pendingQuestion, created_at: new Date().toISOString() }}
              shopperName={myName}
            />
          </li>
        )}
        {live && (
          <li>
            {live.text ? (
              <ChatMessageRow
                message={{ author: 'ai', body: live.text, created_at: new Date().toISOString() }}
                shopperName={myName}
                streaming
              />
            ) : (
              <p className="flex justify-end px-4 py-2 text-xs text-muted-foreground" role="status">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-usa-blue motion-reduce:animate-none"
                  />
                  {live.status || 'Thinking'}…
                </span>
              </p>
            )}
          </li>
        )}
      </ol>
    </div>
  )
}

function Composer({
  busy,
  phone,
  error,
  onSend,
}: {
  busy: boolean
  phone: boolean
  error: string | null
  onSend: (question: string) => void
}) {
  const [text, setText] = useState('')
  const box = useRef<HTMLTextAreaElement>(null)
  const canSend = text.trim().length > 0 && !busy
  const send = () => {
    if (!canSend) return
    onSend(text.trim())
    setText('')
  }
  useEffect(() => {
    if (!busy && !phone) box.current?.focus()
  }, [busy, phone])
  return (
    <div
      className={cn(
        'border-t bg-muted/20',
        phone ? 'px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]' : 'px-3 py-3 sm:px-4',
      )}
    >
      {error && (
        <p role="alert" className="mb-2 flex items-center gap-2 text-sm text-destructive">
          <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
      <div className={cn('flex items-end', phone ? 'gap-1.5' : 'gap-2')}>
        <ComposeTextarea
          ref={box}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              send()
            }
          }}
          rows={phone ? 1 : 2}
          maxLength={4000}
          aria-label="Ask the store AI"
          placeholder="Ask the store AI"
          // 16px on touch screens: iPhones zoom into any smaller text box when it is tapped.
          className={cn(
            'max-h-48 flex-1 resize-none bg-background text-base md:text-base pointer-fine:text-sm md:pointer-fine:text-sm',
            phone ? 'max-h-32 min-h-10 py-2' : 'min-h-[2.75rem]',
          )}
        />
        <LoadingButton
          loading={busy}
          onClick={send}
          disabled={!canSend}
          size={phone ? 'icon' : 'default'}
          className={cn('shrink-0', phone && 'size-10')}
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
          {!phone && 'Send'}
        </LoadingButton>
      </div>
    </div>
  )
}

function Conversation({
  chatId,
  phone,
  asking,
}: {
  chatId: string
  phone: boolean
  asking: ReturnType<typeof useAskStaffChat>
}) {
  const fresh = chatId === NEW_CHAT
  const { data: chat, isLoading, error: loadError } = useStaffChat(fresh ? undefined : chatId)
  const { data: user } = useCurrentUser()
  const { ask, live, error } = asking
  const myName = user?.name || 'You'
  const shownLive = live && (fresh ? !live.chatId : live.chatId === chatId) ? live : null

  if (!fresh && isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }
  if (!fresh && !chat) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {loadError ? 'This chat could not be loaded.' : 'This chat no longer exists.'}
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to="/customer-comms/store-ai">All chats</Link>
        </Button>
      </div>
    )
  }
  const messages = chat?.messages ?? []
  const empty = messages.length === 0 && !shownLive
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {chat ? (
        <ChatHeader chat={chat} phone={phone} />
      ) : (
        phone && (
          <header className="border-b pt-[env(safe-area-inset-top)]">
            <div className="flex items-center gap-2 py-1.5 pl-1 pr-3">
              <Link
                to="/customer-comms/store-ai"
                className="flex size-10 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                aria-label="All chats"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h2 className="text-base font-semibold">New chat</h2>
            </div>
          </header>
        )
      )}
      {empty ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
          <Bot aria-hidden className="h-6 w-6" />
          <p className="text-sm">Ask about any product, shipping, pricing or discounts.</p>
        </div>
      ) : (
        <Messages messages={messages} live={shownLive} myName={myName} />
      )}
      <Composer busy={!!live} phone={phone} error={error} onSend={(question) => void ask(question, chat?.uuid)} />
    </div>
  )
}

export function StaffChatPage({ chatId }: { chatId?: string }) {
  const navigate = useNavigate()
  const { data: chats, isLoading } = useStaffChats()
  // Here, not in the chat pane: a new chat keeps streaming when its address changes to its id.
  const onChat = useCallback(
    (id: string) => void navigate({ to: '/customer-comms/store-ai/$chatId', params: { chatId: id }, replace: true }),
    [navigate],
  )
  const asking = useAskStaffChat(onChat)
  const phone = useMediaQuery(PHONE_QUERY)
  const fullScreen = phone && !!chatId
  const visibleHeight = useVisualViewportHeight(fullScreen)
  const shownId = chatId ?? NEW_CHAT

  return (
    <div
      className={cn(
        'mx-auto flex max-w-[1600px] flex-col gap-3',
        fullScreen
          ? 'h-full'
          : cn('md:h-[calc(100dvh-6.5rem)]', chatId && 'max-md:-m-4 max-md:h-[calc(100dvh-3.5rem)]'),
      )}
      style={visibleHeight ? { height: visibleHeight } : undefined}
    >
      <div className={cn('flex items-center gap-4', chatId && 'max-lg:hidden')}>
        <h1 className="mr-auto text-2xl font-bold tracking-tight">Store AI</h1>
        <Button asChild size="sm" variant="outline">
          <Link to="/customer-comms/store-ai/$chatId" params={{ chatId: NEW_CHAT }}>
            <Plus className="h-4 w-4" />
            New chat
          </Link>
        </Button>
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-hidden bg-card lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)]',
          !fullScreen && 'rounded-lg border',
        )}
      >
        <nav
          aria-label="Your chats"
          className={cn('min-h-0 overflow-y-auto bg-muted/40 p-1.5 lg:border-r', chatId && 'max-lg:hidden')}
        >
          {isLoading ? (
            <div className="space-y-2 p-1">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !chats?.length ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">No chats yet.</p>
          ) : (
            <ul className="space-y-1">
              {chats.map((chat) => (
                <ChatListRow key={chat.uuid} chat={chat} selected={chat.uuid === chatId} />
              ))}
            </ul>
          )}
        </nav>
        <section aria-label="Chat" className={cn('h-full min-h-0 min-w-0', !chatId && 'max-lg:hidden')}>
          <Conversation key={shownId} chatId={shownId} phone={phone} asking={asking} />
        </section>
      </div>
    </div>
  )
}
